/**
 * Calendar Sync Service
 * Automatically syncs offline calendar data to Supabase when connection is restored
 */

import { supabase } from '../lib/supabaseClient';
import OfflineCalendarStorage from './offlineCalendarStorage';
import type { OfflineAppointment, OfflineAppointmentType, OfflineAppointmentNote, OfflinePhoto } from './offlineCalendarStorage';

export interface SyncStatus {
  isSyncing: boolean;
  lastSyncedAt: Date | null;
  pendingCount: number;
  errors: string[];
}

class CalendarSyncService {
  private static instance: CalendarSyncService;
  private offlineStorage = OfflineCalendarStorage.getInstance();
  private isSyncing = false;
  private syncListeners: Set<(status: SyncStatus) => void> = new Set();
  private status: SyncStatus = {
    isSyncing: false,
    lastSyncedAt: null,
    pendingCount: 0,
    errors: []
  };

  private constructor() {
    this.initializeNetworkListener();
  }

  static getInstance(): CalendarSyncService {
    if (!CalendarSyncService.instance) {
      CalendarSyncService.instance = new CalendarSyncService();
    }
    return CalendarSyncService.instance;
  }

  private initializeNetworkListener() {
    window.addEventListener('online', () => {
      console.log('[CalendarSync] Network online - starting sync');
      setTimeout(() => this.syncAll(), 1000); // Delay to ensure connection is stable
    });

    window.addEventListener('offline', () => {
      console.log('[CalendarSync] Network offline');
      this.updateStatus({ isSyncing: false });
    });
  }

  addListener(callback: (status: SyncStatus) => void) {
    this.syncListeners.add(callback);
    callback(this.status); // Immediately call with current status
  }

  removeListener(callback: (status: SyncStatus) => void) {
    this.syncListeners.delete(callback);
  }

  private updateStatus(updates: Partial<SyncStatus>) {
    this.status = { ...this.status, ...updates };
    this.syncListeners.forEach(listener => listener(this.status));
  }

  async syncAll(): Promise<void> {
    if (this.isSyncing || !navigator.onLine) {
      console.log('[CalendarSync] Sync already in progress or offline');
      return;
    }

    this.isSyncing = true;
    this.updateStatus({ isSyncing: true, errors: [] });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Get all pending items
      const [pendingAppointments, pendingPhotos] = await Promise.all([
        this.offlineStorage.getPendingSyncAppointments(),
        this.offlineStorage.getPendingSyncPhotos()
      ]);

      const totalPending = pendingAppointments.length + pendingPhotos.length;
      this.updateStatus({ pendingCount: totalPending });

      console.log(`[CalendarSync] Syncing ${totalPending} items...`);

      // Sync photos first (they might be referenced by appointments)
      for (const photo of pendingPhotos) {
        await this.syncPhoto(photo);
      }

      // Sync appointments
      for (const appointment of pendingAppointments) {
        await this.syncAppointment(appointment, user.id);
      }

      this.updateStatus({
        lastSyncedAt: new Date(),
        pendingCount: 0
      });

      console.log('[CalendarSync] Sync completed successfully');
    } catch (error) {
      console.error('[CalendarSync] Sync failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown sync error';
      this.updateStatus({
        errors: [...this.status.errors, errorMessage]
      });
    } finally {
      this.isSyncing = false;
      this.updateStatus({ isSyncing: false });
    }
  }

  private async syncAppointment(appointment: OfflineAppointment, userId: string): Promise<void> {
    try {
      // Check if this is a new appointment or an update
      const { data: existing } = await supabase
        .from('appointments')
        .select('id')
        .eq('id', appointment.id)
        .single();

      if (existing) {
        // Update existing appointment
        const { error } = await supabase
          .from('appointments')
          .update({
            appointment_type: appointment.appointment_type,
            date: appointment.date,
            time: appointment.time,
            address: appointment.address,
            client_name: appointment.client_name,
            phone_number: appointment.phone_number,
            status: appointment.status,
            photo_url: appointment.photo_url,
            assigned_to_user_id: appointment.assigned_to_user_id,
            updated_at: new Date().toISOString()
          })
          .eq('id', appointment.id);

        if (error) throw error;
      } else {
        // Create new appointment with new server-generated ID
        const { data: newAppointment, error } = await supabase
          .from('appointments')
          .insert([{
            appointment_type: appointment.appointment_type,
            date: appointment.date,
            time: appointment.time,
            address: appointment.address,
            client_name: appointment.client_name,
            phone_number: appointment.phone_number,
            status: appointment.status,
            photo_url: appointment.photo_url,
            user_id: userId,
            assigned_to_user_id: appointment.assigned_to_user_id
          }])
          .select()
          .single();

        if (error) throw error;

        // Update local storage with server ID
        if (newAppointment) {
          await this.offlineStorage.deleteAppointment(appointment.id);
          await this.offlineStorage.saveAppointment(newAppointment, false);
        }
      }

      // Mark as synced if it still exists with offline ID
      await this.offlineStorage.markAsSynced(appointment.id, 'appointments');
      
      console.log(`[CalendarSync] Synced appointment: ${appointment.id}`);
    } catch (error) {
      console.error(`[CalendarSync] Failed to sync appointment ${appointment.id}:`, error);
      throw error;
    }
  }

  private async syncPhoto(photo: OfflinePhoto): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const fileExt = photo.file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('calendar-images')
        .upload(fileName, photo.file);

      if (uploadError) throw uploadError;

      // Update appointment with new photo URL if needed
      if (photo.appointmentId) {
        await supabase
          .from('appointments')
          .update({ photo_url: fileName })
          .eq('id', photo.appointmentId);
      }

      // Mark as synced and delete from offline storage
      await this.offlineStorage.deletePhoto(photo.id);
      
      console.log(`[CalendarSync] Synced photo: ${photo.id}`);
    } catch (error) {
      console.error(`[CalendarSync] Failed to sync photo ${photo.id}:`, error);
      throw error;
    }
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  async forceSyncNow(): Promise<void> {
    if (!navigator.onLine) {
      throw new Error('No internet connection');
    }
    await this.syncAll();
  }
}

export default CalendarSyncService;
